import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Grid, Typography } from "@material-ui/core";
import { FieldSetBox } from "../../fieldSetBox";
import { ReportField } from "../reportField";
import { StatementOfLimitingConditions } from "../stateOfLimitingConditions";
import { CompMap, MapLegend } from "../../mapComponents";
import { InternalValuationReviewStore } from "../../../stores/internalValuationReviewStore";
import { chunkArray, formatCurrencyField, getStringOrDefault } from "../../../utils/utils";
import { ValueReconciliationBox } from "../valueReconciliationBox";

const useStyles = makeStyles(() => ({
  page: {
    fontFamily: "Roboto, Arial, sans-serif",
    border: "1px solid lightgray",
    borderRadius: "5px",
    width: "1018px",
    height: "1440px",
    padding: "64px 40px 64px 40px",
  },
  hiddenStyle: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    opacity: 0,
    height: "450px",
    width: "100%",
  },
  mapContainer: {
    height: "450px",
    width: "100%",
  },
  longTextTypography: {
    fontSize: "14px",
    padding: "16px",
    paddingTop: "10px",
    fontWeight: 400,
  },
}));

interface Page3Props {
  internalValuationReviewStore: InternalValuationReviewStore;
}

function splitMarkers(markers) {
  const firstMarkersArray = markers.slice(0, 11);

  const remainingMarkersArrays =
    markers.length > 11
      ? chunkArray(markers.slice(11), 19)
      : [];

  return { firstMarkersArray, remainingMarkersArrays };
}

export const Page3 = ({ internalValuationReviewStore }: Page3Props) => {
  const classes = useStyles();
  const [reloadMap, setReloadMap] = useState(true);

  const markers = internalValuationReviewStore.getPropertyCompsMarkers();

  const valuationValues: any = internalValuationReviewStore.valuationFormStore.getFlattenedValues();

  const compsCount = (internalValuationReviewStore.propertyCompFormStores || []).length;

  const { firstMarkersArray, remainingMarkersArrays } = splitMarkers(markers);

  useEffect(() => {
    const moveMapToRenderIt = async () => {
      setReloadMap(true);
      await new Promise((resolve) => setTimeout(resolve, 500));
      setReloadMap(false);
    };
    moveMapToRenderIt();
  }, []);
  return (
    <>
      <div className={classes.page}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <FieldSetBox title="Comparable">
              <Box
                className={reloadMap ? classes.hiddenStyle : classes.mapContainer}
              >
                <CompMap
                  markers={markers}
                />
              </Box>
            </FieldSetBox>
          </Grid>
          {compsCount > 0 && (
            <Grid item>
              <FieldSetBox title="Legend">
                <MapLegend markers={firstMarkersArray} />
              </FieldSetBox>
            </Grid>)
          }
          {compsCount <= 4 && (
            <>
              <Grid item xs={12}>
                <ValueReconciliationBox valuationValues={valuationValues} />
              </Grid>
              <Grid item xs={12}>
                <StatementOfLimitingConditions />
              </Grid>
            </>)
          }
        </Grid>
      </div>
      {compsCount > 11 && (
        <>
          {remainingMarkersArrays.map(markers => (
            <div className={classes.page}>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <FieldSetBox title="Legend">
                    <MapLegend markers={markers} />
                  </FieldSetBox>
                </Grid>
              </Grid>
            </div>
          ))}
        </>
      )}
      {compsCount > 4 && (
        <div className={classes.page}>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <FieldSetBox title="Value Reconciliation">
                <Grid container>
                  <Grid item xs={4}>
                    <ReportField
                      label="As-Repaired Value"
                      value={formatCurrencyField(valuationValues.asRepairedValue)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <ReportField
                      label="As-Is Value"
                      value={formatCurrencyField(valuationValues.asIsValue)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <ReportField
                      label="Site Value"
                      value={formatCurrencyField(valuationValues.siteValue)}
                      removeRightBorder
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ReportField
                      label="Value Reconciliation Comments"
                      removeRightBorder
                    >
                      <Typography className={classes.longTextTypography}>
                        {getStringOrDefault(valuationValues.valueReconciliationComments)}
                      </Typography>
                    </ReportField>
                  </Grid>
                </Grid>
              </FieldSetBox>
            </Grid>
            <Grid item xs={12}>
              <StatementOfLimitingConditions />
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};